import { BookmarkPlus, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";


const modeLabels = {
  low_carb: "Low carb",
  balanced: "Balanced",
  flexible: "Flexible",
};


export const MacroPresetPanel = ({
  presets,
  activePresetId,
  activePresetLabel,
  onApplyPreset,
  saveCurrentName,
  onSaveCurrentNameChange,
  onSaveCurrentPreset,
  manualPreset,
  onManualPresetChange,
  onCreateManualPreset,
  busy,
}) => {
  return (
    <section className="space-y-4 rounded-[28px] border border-stone-200 bg-white/90 p-4" data-testid="macro-preset-panel">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" data-testid="macro-preset-kicker">
          Macro presets
        </p>
        <h3 className="mt-2 text-lg font-semibold text-stone-900" data-testid="macro-preset-title">
          Low Carb, Balanced, Family, Fat Loss en je eigen Custom presets.
        </h3>
        <p className="mt-2 text-sm text-stone-600" data-testid="macro-preset-description">
          Presets sturen op calorie-target, carb-target, max carbs per meal en priority mode.
        </p>
      </div>

      <div className="sticky top-3 z-[5] inline-flex w-fit max-w-full items-center rounded-full border border-stone-200 bg-white/95 px-3 py-2 text-xs font-medium text-stone-700 shadow-sm" data-testid="macro-preset-active-label">
        Actief in profiel: <span className="ml-1 font-semibold text-stone-900" data-testid="macro-preset-active-label-value">{activePresetLabel || "Handmatig"}</span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2" data-testid="macro-preset-grid">
        {presets.map((preset) => (
          <button
            key={preset.id}
            type="button"
            onClick={() => onApplyPreset(preset)}
            className={`rounded-[24px] border px-4 py-4 text-left transition-colors hover:bg-stone-100 ${activePresetId === preset.id ? "border-emerald-300 bg-emerald-50" : "border-stone-200 bg-stone-50"}`}
            data-testid={`macro-preset-card-${preset.id}`}
          >
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-stone-900">{preset.name}</p>
              <div className="flex items-center gap-2">
                {activePresetId === preset.id ? <span className="rounded-full bg-emerald-700 px-2.5 py-1 text-[11px] font-medium text-white" data-testid={`macro-preset-active-badge-${preset.id}`}>Actief</span> : null}
                <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-stone-600">
                  {preset.is_system ? "System" : "Custom"}
                </span>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-stone-600">
              <span className="rounded-full bg-white px-3 py-1">{preset.calorie_target_daily} kcal</span>
              <span className="rounded-full bg-white px-3 py-1">{preset.daily_carb_target} g carbs</span>
              <span className="rounded-full bg-white px-3 py-1">{preset.max_carbs_per_meal || "geen"} max/meal</span>
            </div>
            <p className="mt-3 text-xs text-stone-500">Mode: {modeLabels[preset.carb_priority_mode]}</p>
          </button>
        ))}
      </div>

      <div className="rounded-[24px] border border-dashed border-stone-300 bg-stone-50 p-4" data-testid="macro-preset-save-current-card">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-4 w-4 text-emerald-700" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-stone-900">Huidige instellingen als Custom opslaan</p>
            <p className="mt-1 text-sm text-stone-600">Sla je huidige target-config op en hergebruik hem later.</p>
          </div>
        </div>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <Input value={saveCurrentName} onChange={(event) => onSaveCurrentNameChange(event.target.value)} placeholder="Bijv. Demo low carb week" className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="macro-preset-save-current-name-input" />
          <Button className="h-12 rounded-full bg-stone-900 text-white hover:bg-stone-800" onClick={onSaveCurrentPreset} disabled={busy} data-testid="macro-preset-save-current-button">
            <BookmarkPlus className="mr-2 h-4 w-4" />
            Bewaar huidige preset
          </Button>
        </div>
      </div>

      <div className="rounded-[24px] border border-dashed border-stone-300 bg-stone-50 p-4" data-testid="macro-preset-manual-card">
        <p className="text-sm font-semibold text-stone-900">Maak handmatig een Custom preset</p>
        <div className="mt-3 space-y-3">
          <Input value={manualPreset.name} onChange={(event) => onManualPresetChange("name", event.target.value)} placeholder="Presetnaam" className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="macro-preset-manual-name-input" />
          <div className="grid grid-cols-2 gap-3">
            <Input type="number" min="1200" max="4500" value={manualPreset.calorie_target_daily} onChange={(event) => onManualPresetChange("calorie_target_daily", event.target.value)} className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="macro-preset-manual-calorie-input" />
            <Input type="number" min="40" max="400" value={manualPreset.daily_carb_target} onChange={(event) => onManualPresetChange("daily_carb_target", event.target.value)} className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="macro-preset-manual-carb-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input type="number" min="10" max="200" value={manualPreset.max_carbs_per_meal} onChange={(event) => onManualPresetChange("max_carbs_per_meal", event.target.value)} placeholder="Max carbs/meal" className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="macro-preset-manual-max-carb-input" />
            <Select value={manualPreset.carb_priority_mode} onValueChange={(value) => onManualPresetChange("carb_priority_mode", value)}>
              <SelectTrigger className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="macro-preset-manual-mode-select">
                <SelectValue placeholder="Carb-priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low_carb">Low Carb</SelectItem>
                <SelectItem value="balanced">Balanced</SelectItem>
                <SelectItem value="flexible">Flexible</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button className="w-full rounded-full bg-emerald-700 text-white hover:bg-emerald-800" onClick={onCreateManualPreset} disabled={busy} data-testid="macro-preset-manual-save-button">
            Maak custom preset
          </Button>
        </div>
      </div>
    </section>
  );
};